import { computed } from 'vue'
import { ColumnDef, ColumnDefByColName } from '../../CORE/src/common/$models/ModelDef'
import {
  ModelPsAgreementRequests as ModelPsAgreementRequestsOriginal,
  ModelPsAgreementSections,
  ModelPsAgreementSectionTemplates,
  ModelPsAgreementTemplate,
  ModelPsAgreementTypes,
  ModelPsAnimalTypes,
  ModelPsClinics,
} from '../../models/fireModels/types'

export type AgreementFlowDefinitionValueTemplateUserDefineItem = Omit<
  ModelPsAgreementSectionTemplates['user_define_items'][number],
  'type'
> & {
  type: userDefineItemsInputTypes
  value: any
}

export type AgreementFlowDefinitionValueTemplateByIdItem = {
  template: ModelPsAgreementSectionTemplates
  selected: boolean
  userDefineItemsByKey: {
    [key: string]: AgreementFlowDefinitionValueTemplateUserDefineItem
  }
  forceLetClinicUserInputOnSimpleSend?: boolean
}
export type AgreementFlowDefinitionValueTemplateById = {
  [templateId: string]: AgreementFlowDefinitionValueTemplateByIdItem
}

export type AgreementFlowDefinitionValueSectionByIdItem = {
  section: ModelPsAgreementSections
  selectableTemplateById: AgreementFlowDefinitionValueTemplateById
  excluded: boolean
}

export type AgreementFlowDefinitionValueSectionById = {
  [sectionId: string]: AgreementFlowDefinitionValueSectionByIdItem
}

export type AgreementFlowDefinitionValue = {
  agreementTypeId: string
  agreementType?: ModelPsAgreementTypes
  sectionById: AgreementFlowDefinitionValueSectionById
}
// Overwrite
export type ModelPsAgreementRequests = ModelPsAgreementRequestsOriginal & {
  agreement_content: AgreementFlowDefinitionValue
}

const getAgreementFlowDefinitionDefault = (): AgreementFlowDefinitionValue => ({
  agreementTypeId: '',
  sectionById: {},
})

/**
 * 同意フローのプレビューを表示するモーダルを開く
 * @param modelName { string } モデル名
 * @param record { any } モデルのレコード
 */
export const openAgreementFlowPreviewModalWithPsAgreementRequest = async (
  record?: Partial<ModelPsAgreementRequests>,
  templateData?: Partial<ModelPsAgreementTemplate>,
) => {
  const commonModalProps = {
    size: 'md',
    title: '同意フローのプレビュー',
  } as any
  // 医院情報が 入っていなければ (id 値のみであれば) Set する
  // @ts-ignore
  if (!record?.clinic_id?.id && record?.clinic_id) {
    const clinic =
      (
        await $core.$models.ps_clinics.find({
          filter: {
            id: { _eq: record.clinic_id },
          },
        })
      )?.[0] || null
    if (clinic) {
      record.clinic_id = clinic
    }
  }
  $core.$modals.openModal({
    modalProps: commonModalProps,
    component: {
      template: `
<div class="text-center mb-2" @click.prevent="() => $refs.printModal.show()">
  <span class="btn btn-outline-secondary">
    <ficon type="print" /> 印刷用画面を表示
  </span>
</div>
<AgreementFlowWithAgreementRequestId v-if="hasId" v-bind="bindValues"/>
<AgreementFlowWithAgreementTemplateData v-if="!hasId" v-bind="bindValues"/>
<b-modal ref="printModal" title="印刷用画面" size="fullscreen" :hide-footer="true" :hide-header="true">
  <div class="text-center">
    <span class="btn btn-outline-secondary" @click.prevent="() => $core.$utils.printElement($refs.printAgreementFlowView)">
      <ficon type="print" /> 印刷する
    </span>
    <span class="btn btn-outline-secondary ml-2" @click.prevent="() => $refs.printModal.hide()">
      閉じる
    </span>
  </div>
  <div ref="printAgreementFlowView">
    <AgreementFlowWithAgreementRequestId v-if="hasId" v-bind="bindValues" :shouldPreviewAsPrint="true"/>
    <AgreementFlowWithAgreementTemplateData v-if="!hasId" v-bind="bindValues" :shouldPreviewAsPrint="true"/>
  </div>
</b-modal>
`,
      computed: {
        hasId(): boolean {
          return !!record?.id
        },
        bindValues() {
          // TODO: コンポーネント props の typo で a と A が混在...
          return {
            AgreementFlowWithAgreementRequestId: record?.id,
            AgreementRequestData: record,
            AgreementTemplateData: templateData,
            agreementRequestData: record,
            agreementTemplateData: templateData,
          }
        },
      },
    },
  })
}

export const openAgreementFlowPreviewModalWithPsAgreementTemplate = async (
  record: Partial<ModelPsAgreementTemplate>,
) => {
  const commonModalProps = {
    size: 'md',
    title: '同意フローのプレビュー',
  } as any

  $core.$modals.openModal({
    modalProps: commonModalProps,
    component: {
      template: `<AgreementFlowWithAgreementTemplateData v-bind="bindValues"/>`,
      computed: {
        bindValues() {
          return {
            agreementTemplateData: record,
          }
        },
      },
    },
  })
}

const convertAgreementTypeToAgreementFlowDefinition = (
  agreementType: ModelPsAgreementTypes,
  setDefaultSelectedIfSelectionIsOnlyOne: boolean = true,
  mergeValue: AgreementFlowDefinitionValue | null = null,
): AgreementFlowDefinitionValue => {
  if (mergeValue?.agreementTypeId !== agreementType.id) {
    mergeValue = null // 既存の value が 異なる agreementTypeId の場合は、 mergeValue を null にする
  }
  return {
    agreementTypeId: agreementType.id as string,
    agreementType,
    sectionById:
      agreementType.sections?.reduce(
        (acc: AgreementFlowDefinitionValue['sectionById'], section) => {
          acc[section.id as string] = {
            section,
            excluded: mergeValue?.sectionById[section.id as string]?.excluded || false,
            selectableTemplateById:
              section.selectableTemplates?.reduce(
                (templateById: AgreementFlowDefinitionValueTemplateById, template) => {
                  const selected = mergeValue
                    ? mergeValue.sectionById[section.id as string]
                        ?.selectableTemplateById[template.id as number]?.selected
                    : !!(
                        setDefaultSelectedIfSelectionIsOnlyOne &&
                        section.selectableTemplates?.length === 1
                      )
                  const forceLetClinicUserInputOnSimpleSend =
                    mergeValue?.sectionById[section.id as string]?.selectableTemplateById[
                      template.id as number
                    ]?.forceLetClinicUserInputOnSimpleSend === true
                  templateById[template.id as number] = {
                    template,
                    selected,
                    userDefineItemsByKey:
                      template.user_define_items?.reduce((inputsByKey, input) => {
                        if (!input.key) {
                          return inputsByKey
                        }
                        inputsByKey[input.key] = {
                          name: input.name,
                          type: input.type,
                          isRequired: input.isRequired,
                          selectOptions: input.selectOptions,
                          value:
                            mergeValue?.sectionById[section.id as string]
                              ?.selectableTemplateById[template.id as number]
                              ?.userDefineItemsByKey[input.key]?.value || '',
                        }
                        return inputsByKey
                      }, {}) || {},
                    forceLetClinicUserInputOnSimpleSend,
                  }
                  return templateById
                },
                {} as AgreementFlowDefinitionValueTemplateById,
              ) || {},
          }
          return acc
        },
        {},
      ) || {},
  }
}

/**
 * 同意フローの設定時に使用するサービス
 */
export class AgreementFlowDefinitionInputService {
  value: AgreementFlowDefinitionValue = getAgreementFlowDefinitionDefault()
  $inputVueInstance: any
  fetchedAgreementTypeWithSectionAndTemplates: ModelPsAgreementTypes | null = null
  isLoading: boolean = false
  validationErrors: {
    errorsBySectionId: { [sectionId: string]: string[] }
    errorsByTemplateId: { [templateId: string]: string[] }
    errorsBySectionIdAndTemplateIdAndInputKey: { [key: string]: string[] }
  } = {
    errorsBySectionId: {},
    errorsByTemplateId: {},
    errorsBySectionIdAndTemplateIdAndInputKey: {},
  }
  get errorMessages(): string[] {
    return Object.keys(this.validationErrors).reduce((acc, key) => {
      // @ts-ignore
      return acc.concat(...Object.values(this.validationErrors[key]))
    }, [])
  }
  allAnimalTypesById: { [animalTypeId: string]: ModelPsAnimalTypes } = {}
  options: {
    forceValueOverwrite: boolean
    refreshTemplate: boolean
  } = {
    forceValueOverwrite: false,
    refreshTemplate: false,
  }

  get editingModelName(): string {
    return this.$inputVueInstance.modelName
  }
  get isEditingTemplate(): boolean {
    return this.editingModelName === 'ps_agreement_template'
  }

  constructor(
    currentValue: AgreementFlowDefinitionValue,
    $AgreementFlowDefinitionInputVueInstance: any,
    options: AgreementFlowDefinitionInputService['options'] = {
      forceValueOverwrite: false,
      refreshTemplate: false,
    },
  ) {
    this.options = options
    this.value =
      typeof currentValue === 'object'
        ? currentValue
        : getAgreementFlowDefinitionDefault()
    this.$inputVueInstance = $AgreementFlowDefinitionInputVueInstance
  }

  get currentEditingRecord(): ModelPsAgreementRequests {
    return this.$inputVueInstance.record
  }
  get agreement_type_id(): string {
    return this.currentEditingRecord.agreement_type_id as string
  }

  async init() {
    this.isLoading = true
    await Promise.all([
      this.loadAgreementTypeWithSectionAndTemplatesIfNeeded(),
      this.loadAnimalTypes(),
    ])

    this.isLoading = false
  }

  async loadAgreementTypeWithSectionAndTemplatesIfNeeded(
    forceValueOverwrite: boolean = this.options.forceValueOverwrite,
  ) {
    this.fetchedAgreementTypeWithSectionAndTemplates =
      (
        await $core.$models.ps_agreement_types.find({
          filter: {
            id: { _eq: this.agreement_type_id },
          },
          fields: [
            '*',
            'sections.*',
            'sections.selectableTemplates.*',
            'sections.selectableTemplates.flow_pages.*',
          ],
        })
      )[0] || null
    if (!this.fetchedAgreementTypeWithSectionAndTemplates) {
      $core.$toast.warningToast('同意種別マスタが見つかりませんでした')
      return
    }
    // this.value を初期化
    if (this.value?.agreementTypeId !== this.agreement_type_id || forceValueOverwrite) {
      this.value = convertAgreementTypeToAgreementFlowDefinition(
        this.fetchedAgreementTypeWithSectionAndTemplates,
      )
      this.emitValue()
    } else {
      if (this.options.refreshTemplate) {
        // 既存の value に対して、新しい agreementTypeWithSectionAndTemplates で更新する処理
        this.value = convertAgreementTypeToAgreementFlowDefinition(
          this.fetchedAgreementTypeWithSectionAndTemplates,
          true,
          this.value,
        )
      }
      this.emitValue()
    }
  }
  get sections(): ModelPsAgreementSections[] {
    return (
      (
        this.value.sectionById &&
        Object.values(this.value.sectionById).map(item => item.section)
      ).sort((a, b) => (a.order || 0) - (b.order || 0)) || []
    )
  }

  async loadAnimalTypes() {
    this.allAnimalTypesById = (
      await $core.$models.ps_animal_types.find({
        limit: -1,
      })
    ).reduce((acc, item) => {
      acc[item.id as string] = item
      return acc
    }, {})
  }

  public toggleTemplateSelectedState(sectionId: string, templateId: number) {
    const template = this.value.sectionById[sectionId]?.selectableTemplateById[templateId]
    if (!template) {
      return // do nothing
    }
    const currentSelected = !template.selected
    this.setTemplateSelectedState(sectionId, templateId, currentSelected)
  }

  public setTemplateSelectedState(
    sectionId: string,
    templateId: number,
    selected: boolean,
  ) {
    const selectType = this.value.sectionById[sectionId]?.section
      ?.sectionTemplateSelectType
    if (selectType === '択一') {
      Object.values(
        this.value.sectionById[sectionId]?.selectableTemplateById || {},
      ).forEach(template => {
        if (template.template.id === templateId) {
          template.selected = selected // false でも true でもどちらでもいい
        } else {
          template.selected = false
        }
      })
    } else if (selectType === '複数選択') {
      const template = this.value.sectionById[sectionId]?.selectableTemplateById[
        templateId
      ]
      if (!template) {
        return // do nothing
      }
      template.selected = selected
    }
    this.emitValue()
  }

  public setForceLetClinicUserInputOnSimpleSend(
    sectionId: string,
    templateId: number,
    selected: boolean,
  ) {
    const section = this.value.sectionById[sectionId]?.selectableTemplateById[templateId]
    if (!section) {
      return // do nothing
    }
    section.forceLetClinicUserInputOnSimpleSend = !!selected
    this.emitValue()
  }

  public toggleSectionExcludedState(sectionId: string) {
    const section = this.value.sectionById[sectionId]
    if (!section) {
      return // do nothing
    }
    section.excluded = !section.excluded
    this.emitValue()
  }

  public updateUserDefineItems(
    sectionId: string,
    templateId: number,
    record: Record<string, any>,
  ) {
    const template = this.value.sectionById[sectionId]?.selectableTemplateById[templateId]
    if (!template) {
      return // do nothing
    }
    Object.entries(record).forEach(([key, value]) => {
      const input = template.userDefineItemsByKey[key]
      if (!input) {
        return // do nothing
      }
      input.value = value
    })
    this.emitValue()
  }

  emitValue() {
    this.calcValidationErrors()
    this.$inputVueInstance.emitValue()
  }

  /**
   * テンプレートのデータ 及び 現在の レコードの状態 (this.currentEditingRecord) によって、表示 / 非表示 可否を判断する
   * @param template
   * @returns
   */
  judgeShouldEnabledByTemplateData(template: ModelPsAgreementSectionTemplates): boolean {
    if (!template.specifyAgreementCondition) {
      return true
    }
    if (
      template.agreementFilter_pet &&
      Object.keys(template.agreementFilter_pet).length > 0
    ) {
      // 同意書の ペットの状態 による 出し分け
      const petId = this.currentEditingRecord.pet_type as string
      const pet = this.allAnimalTypesById[petId]
      if (pet) {
        const filtered = $core.$utils.filterRecordsByFilterQuery(
          template.agreementFilter_pet,
          [pet],
        )
        return filtered.length > 0
      }
      return false
    }
    // 医院の状態を判別
    if (
      template.agreementFilter_clinic &&
      Object.keys(template.agreementFilter_clinic).length > 0 &&
      this.currentUserClinic
    ) {
      const filtered = $core.$utils.filterRecordsByFilterQuery(
        template.agreementFilter_clinic,
        [this.currentUserClinic],
      )
      return filtered.length > 0
    }
    return true
  }

  get currentUserClinic(): ModelPsClinics | null {
    // @ts-ignore
    return $core.$embAuth.user?.ps_clinic || null
  }

  calcValidationErrors() {
    const errorsBySectionId: { [sectionId: string]: string[] } = {}
    const errorsByTemplateId: { [templateId: number]: string[] } = {}
    const errorsBySectionIdAndTemplateIdAndInputKey: {
      [key: number]: string[]
    } = {}
    // 1. 同意セクション内で 除外でなく、 selected な template が 1 つもない場合はエラー
    Object.entries(this.value.sectionById).forEach(([sectionId, section]) => {
      if (
        !section.excluded &&
        !Object.values(section.selectableTemplateById).some(template => template.selected)
      ) {
        errorsBySectionId[sectionId] = ['選択してください']
      }
    })
    // 2. 同意セクション内 選択された Template で user_define_items が必須のものが未入力の場合はエラー
    Object.entries(this.value.sectionById).forEach(([sectionId, section]) => {
      if (section.excluded) {
        return // do nothing
      }
      Object.values(section.selectableTemplateById).forEach(template => {
        if (template.selected) {
          const keys = Object.keys(template.userDefineItemsByKey || {})
          if (template.forceLetClinicUserInputOnSimpleSend) {
            // 必須入力だが template 編集中に forceLetClinicUserInputOnSimpleSend: true なら、エラーにしない
            if (this.isEditingTemplate) {
              return // do nothing
            }
          }
          if (keys.length > 0) {
            Object.entries(template.userDefineItemsByKey).forEach(([key, input]) => {
              if (input.isRequired && !input.value) {
                const errorKey = `${sectionId}-${template.template.id}-${key}`
                // errorsByTemplateId[`${sectionId}-${template.template.id}-${input.name}`] = ['必須項目です']
                errorsBySectionIdAndTemplateIdAndInputKey[errorKey] =
                  errorsBySectionIdAndTemplateIdAndInputKey[errorKey] || []
                errorsBySectionIdAndTemplateIdAndInputKey[errorKey].push(
                  `${input.name} は必須です`,
                )
              }
            })
          }
        }
      })
    })
    this.validationErrors = {
      errorsBySectionId,
      errorsByTemplateId,
      errorsBySectionIdAndTemplateIdAndInputKey,
    }
  }

  // helper method for userDefineItemsByKey
  convertUserDefineItemsByKeyToColumnDefByColName = convertUserDefineItemsByKeyToColumnDefByColName

  async forceRefreshWithLatestAgreementType(showConfirmDialog: boolean = true) {
    if (
      showConfirmDialog !== false &&
      (await $core.$toast.confirmDialog(
        `最新の同意種別マスタの情報で 同意内容を上書きします。この操作は医院入力内容や、過去の同意書内容テキストを消去し、もとに戻すことはできません。よろしいですか？`,
      )) === false
    ) {
      return // do nothing
    }
    await this.loadAgreementTypeWithSectionAndTemplatesIfNeeded(true)
  }
}

type userDefineItemsInputTypes =
  | 'STRING'
  | 'NUMBER'
  | 'TEXT'
  | 'RICHTEXT'
  | 'SELECT'
  | 'MULTISELECT'
  | 'RADIO'
  | 'DATEONLY'

const userDefineItemsInputTypeAndColumnDefTypeMap: {
  [key in userDefineItemsInputTypes]: { type: ColumnDef['type'] } & Partial<ColumnDef>
} = {
  STRING: { type: 'STRING', width: { xs: 48, sm: 24 } },
  NUMBER: { type: 'NUMBER', width: { xs: 48, sm: 24 } },
  TEXT: { type: 'TEXT', width: { xs: 48 } },
  RICHTEXT: { type: 'RICHTEXT', width: { xs: 48 } },
  SELECT: { type: 'STRING', width: { xs: 48, sm: 24 } },
  MULTISELECT: { type: 'MULTISELECT', width: { xs: 48, sm: 24 } },
  RADIO: {
    type: 'STRING',
    inputComponent: 'RadioSelectInput',
    inputAttrs: { wrapperClass: 'flex-grow-1' },
  },
  DATEONLY: {
    type: 'DATEONLY',
    width: { xs: 48, sm: 24 },
  },
}

const convertUserDefineItemsByKeyToColumnDefByColName = (
  userDefineItemsByKey: AgreementFlowDefinitionValueTemplateByIdItem['userDefineItemsByKey'],
): ColumnDefByColName => {
  return Object.entries(userDefineItemsByKey).reduce(
    (acc, [key, input], currentIndex) => {
      acc[key] = {
        label: input.name,
        ...userDefineItemsInputTypeAndColumnDefTypeMap[input.type],
      }
      // 幅の強制
      if (input.isRequired) {
        acc[key].validate = { notEmpty: true }
      }
      if (input.selectOptions) {
        acc[key].selections = () =>
          input.selectOptions
            .split('\n')
            .map(l => l.trim())
            .filter(l => l)
      }
      // acc[key].orderOnForm = currentIndex + 1
      return acc
    },
    {} as ColumnDefByColName,
  )
}
